import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Calendar, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { differenceInDays, parseISO } from 'date-fns';

interface InvoiceDueBadgeProps {
  dueDate?: string | null;
  status: string;
  paidDate?: string | null;
  className?: string;
}

export const InvoiceDueBadge: React.FC<InvoiceDueBadgeProps> = ({
  dueDate,
  status,
  paidDate,
  className
}) => {
  // Don't show overdue badge if invoice is paid or void
  if (!dueDate || status === 'PAID' || status === 'paid' || status === 'VOID' || status === 'void' || paidDate) {
    return null;
  }

  const today = new Date();
  const due = parseISO(dueDate);
  const daysUntilDue = differenceInDays(due, today);

  let badgeConfig;
  if (daysUntilDue < 0) {
    // Overdue
    badgeConfig = {
      label: `${Math.abs(daysUntilDue)} days overdue`,
      labelGR: `${Math.abs(daysUntilDue)} μέρες εκπρόθεσμο`,
      variant: 'destructive' as const,
      icon: AlertTriangle,
      color: 'text-white'
    };
  } else if (daysUntilDue === 0) {
    // Due today
    badgeConfig = {
      label: 'Due today',
      labelGR: 'Λήγει σήμερα',
      variant: 'default' as const,
      icon: AlertTriangle,
      color: 'text-orange-600'
    };
  } else if (daysUntilDue <= 7) {
    // Due soon (within 7 days)
    badgeConfig = {
      label: `Due in ${daysUntilDue} days`,
      labelGR: `Λήγει σε ${daysUntilDue} μέρες`,
      variant: 'secondary' as const,
      icon: Calendar,
      color: 'text-yellow-600'
    };
  } else {
    // Due later (more than 7 days)
    badgeConfig = {
      label: `Due in ${daysUntilDue} days`,
      labelGR: `Λήγει σε ${daysUntilDue} μέρες`,
      variant: 'outline' as const,
      icon: Calendar,
      color: 'text-muted-foreground'
    };
  }

  const Icon = badgeConfig.icon;

  return (
    <Badge 
      variant={badgeConfig.variant}
      className={cn('flex items-center gap-1.5 text-xs', className)}
    >
      <Icon className={cn('h-3 w-3', badgeConfig.color)} />
      <span>{badgeConfig.label}</span>
    </Badge>
  );
};